import React from 'react';
import { useConfigStore } from '@stores/configStore';
import { Server, Cloud, Globe, Zap, Users } from 'lucide-react';

interface PlatformOption {
  id: 'EBS' | 'FUSION' | 'NETSUITE' | 'JDE' | 'PSFT';
  name: string;
  version: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const PLATFORMS: PlatformOption[] = [
  {
    id: 'EBS',
    name: 'Oracle E-Business Suite',
    version: '12.x',
    description: 'On-premise ERP system for large enterprises',
    icon: <Server className="w-8 h-8" />,
    color: 'bg-blue-50 border-blue-200',
  },
  {
    id: 'FUSION',
    name: 'Oracle Fusion Cloud ERP',
    version: 'Cloud',
    description: 'Cloud-based modern ERP platform',
    icon: <Cloud className="w-8 h-8" />,
    color: 'bg-purple-50 border-purple-200',
  },
  {
    id: 'NETSUITE',
    name: 'Oracle NetSuite',
    version: 'Cloud',
    description: 'Cloud-based financial and business management',
    icon: <Globe className="w-8 h-8" />,
    color: 'bg-green-50 border-green-200',
  },
  {
    id: 'JDE',
    name: 'Oracle JD Edwards',
    version: 'EnterpriseOne',
    description: 'Integrated ERP for mid-market enterprises',
    icon: <Zap className="w-8 h-8" />,
    color: 'bg-orange-50 border-orange-200',
  },
  {
    id: 'PSFT',
    name: 'Oracle PeopleSoft',
    version: 'Cloud',
    description: 'Human capital and financial management',
    icon: <Users className="w-8 h-8" />,
    color: 'bg-red-50 border-red-200',
  },
];

export const PlatformSelectionForm: React.FC = () => {
  const { config, updatePlatform } = useConfigStore();
  const selectedPlatform = config.platform.selectedPlatform;

  const handlePlatformChange = (platformId: 'EBS' | 'FUSION' | 'NETSUITE' | 'JDE' | 'PSFT') => {
    const platform = PLATFORMS.find((p) => p.id === platformId);
    if (platform) {
      updatePlatform({
        selectedPlatform: platformId,
        platformName: platform.name,
        platformVersion: platform.version,
        platformDescription: platform.description,
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Oracle Platform</h2>
        <p className="text-gray-600">Choose the Oracle ERP platform you want to integrate with</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {PLATFORMS.map((platform) => (
          <button
            key={platform.id}
            onClick={() => handlePlatformChange(platform.id)}
            className={`p-6 rounded-lg border-2 transition-all text-left ${
              selectedPlatform === platform.id
                ? `${platform.color} border-current shadow-lg`
                : 'bg-white border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="text-gray-700">{platform.icon}</div>
              {selectedPlatform === platform.id && (
                <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>

            <h3 className="font-semibold text-gray-900 mb-1">{platform.name}</h3>
            <p className="text-sm text-gray-600 mb-3">{platform.description}</p>

            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                {platform.version}
              </span>
              {selectedPlatform === platform.id && (
                <span className="text-xs font-medium text-blue-600">Selected</span>
              )}
            </div>
          </button>
        ))}
      </div>

      {selectedPlatform && (
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">Selected Platform</h4>
          <p className="text-blue-800">
            {PLATFORMS.find((p) => p.id === selectedPlatform)?.name} ({PLATFORMS.find((p) => p.id === selectedPlatform)?.version})
          </p>
          <p className="text-sm text-blue-700 mt-2">
            Configure the connection settings for this platform in the other tabs.
          </p>
        </div>
      )}
    </div>
  );
};
